import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserCog, Plus, X, KeyRound, UserMinus, Loader2, Mail, Phone } from 'lucide-react'
import toast from 'react-hot-toast'
import { staff, auth } from '../api/endpoints.js'
import useAuthStore from '../store/authStore.js'

// matches the StaffRole enum in the API exactly
const ROLES = ['owner', 'admin', 'coach', 'front_desk', 'viewer']

function Dialog({ title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/55 backdrop-blur-sm animate-fade-in p-4"
         onClick={onClose}>
      <div className="card w-full max-w-md animate-pop max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
        <div className="flex justify-end gap-2 mt-5">{footer}</div>
      </div>
    </div>
  )
}

function NewStaffDialog({ onClose }) {
  const qc = useQueryClient()
  const [f, setF] = useState({
    first_name: '', last_name: '', email: '', phone: '', role: 'coach', password: '',
  })
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))

  const create = useMutation({
    mutationFn: () => staff.create({ ...f, phone: f.phone || null }),
    onSuccess: () => {
      toast.success('Staff account created')
      qc.invalidateQueries({ queryKey: ['staff'] })
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not create account'),
  })

  return (
    <Dialog
      title="New staff account"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={create.isPending || !f.first_name || !f.email || !f.password}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create account'}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="s-first">First name</label>
          <input id="s-first" className="input-field" value={f.first_name} onChange={set('first_name')} />
        </div>
        <div>
          <label className="label" htmlFor="s-last">Last name</label>
          <input id="s-last" className="input-field" value={f.last_name} onChange={set('last_name')} />
        </div>
        <div className="col-span-2">
          <label className="label" htmlFor="s-email">Email</label>
          <input id="s-email" type="email" className="input-field" value={f.email} onChange={set('email')} />
        </div>
        <div>
          <label className="label" htmlFor="s-phone">Phone</label>
          <input id="s-phone" className="input-field" value={f.phone} onChange={set('phone')} />
        </div>
        <div>
          <label className="label" htmlFor="s-role">Role</label>
          <select id="s-role" className="input-field" value={f.role} onChange={set('role')}>
            {ROLES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label" htmlFor="s-pass">Temporary password</label>
          <input id="s-pass" type="password" className="input-field" value={f.password} onChange={set('password')} />
        </div>
      </div>
    </Dialog>
  )
}

function ChangePasswordDialog({ onClose }) {
  const [cur, setCur] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  const go = useMutation({
    mutationFn: () => auth.changePassword(cur, next),
    onSuccess: () => { toast.success('Password changed'); onClose() },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not change password'),
  })

  const mismatch = next && confirm && next !== confirm

  return (
    <Dialog
      title="Change my password"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary"
                  disabled={go.isPending || !cur || !next || mismatch}
                  onClick={() => go.mutate()}>
            {go.isPending ? 'Saving…' : 'Change password'}
          </button>
        </>
      }
    >
      <label className="label" htmlFor="p-cur">Current password</label>
      <input id="p-cur" type="password" className="input-field mb-3" value={cur} onChange={(e) => setCur(e.target.value)} />
      <label className="label" htmlFor="p-new">New password</label>
      <input id="p-new" type="password" className="input-field mb-3" value={next} onChange={(e) => setNext(e.target.value)} />
      <label className="label" htmlFor="p-cfm">Confirm new password</label>
      <input id="p-cfm" type="password" className="input-field" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      {mismatch && <p className="text-xs mt-2" style={{ color: 'rgb(var(--danger))' }}>Passwords do not match.</p>}
    </Dialog>
  )
}

export default function StaffPage() {
  const qc = useQueryClient()
  const me = useAuthStore((s) => s.staff)
  const [showNew, setShowNew] = useState(false)
  const [showPass, setShowPass] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [busyId, setBusyId] = useState(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['staff', { showInactive }],
    queryFn: () => staff.list({ include_inactive: showInactive }).then((r) => r.data),
  })

  const list = Array.isArray(data) ? data : data?.items ?? []

  const changeRole = useMutation({
    mutationFn: ({ id, role }) => staff.update(id, { role }),
    onMutate: ({ id }) => setBusyId(id),
    onSuccess: () => { toast.success('Role updated'); qc.invalidateQueries({ queryKey: ['staff'] }) },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not update role'),
    onSettled: () => setBusyId(null),
  })

  const deactivate = useMutation({
    mutationFn: (id) => staff.deactivate(id),
    onMutate: (id) => setBusyId(id),
    onSuccess: () => { toast.success('Account deactivated'); qc.invalidateQueries({ queryKey: ['staff'] }) },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not deactivate'),
    onSettled: () => setBusyId(null),
  })

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Staff</h1>
        <div className="flex items-center gap-2">
          <button className="btn-secondary inline-flex items-center gap-2" onClick={() => setShowPass(true)}>
            <KeyRound className="w-4 h-4" /> My password
          </button>
          <button className="btn-primary inline-flex items-center gap-2" onClick={() => setShowNew(true)}>
            <Plus className="w-4 h-4" /> Add staff
          </button>
        </div>
      </div>

      <label className="inline-flex items-center gap-2 text-sm text-text-muted cursor-pointer select-none">
        <input type="checkbox" className="accent-[rgb(var(--primary))]"
               checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
        Show deactivated accounts
      </label>

      {error ? (
        <div className="card" style={{ borderColor: 'rgb(var(--danger-border))' }}>
          <p className="font-semibold" style={{ color: 'rgb(var(--danger))' }}>Could not load staff</p>
          <p className="text-sm text-text-muted mt-1">
            {error?.response?.status === 404
              ? 'The GET /auth/staff endpoint is missing on this build of the API.'
              : error?.response?.data?.detail || error.message}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table-base">
            <thead className="table-head">
              <tr>
                <th>Name</th><th>Contact</th><th>Role</th><th>Status</th><th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="skeleton h-4 w-3/4" /></td>
                    ))}
                  </tr>
                ))
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-14 text-center text-text-dim">
                    <UserCog className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No staff accounts.
                  </td>
                </tr>
              ) : (
                list.map((s) => {
                  const busy = busyId === s.id
                  const isMe = me?.staff_id === s.id || me?.id === s.id
                  const name = s.full_name || `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || '—'
                  return (
                    <tr key={s.id} className="hover:bg-surface-3/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="grid place-items-center w-8 h-8 rounded-full bg-primary/15 text-primary text-xs font-bold shrink-0">
                            {name.split(/\s+/).slice(0, 2).map((x) => x[0]).join('').toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-text-primary">{name}</div>
                            {isMe && <div className="text-[11px] text-primary font-semibold">you</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {s.email && <div className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3" />{s.email}</div>}
                        {s.phone && <div className="inline-flex items-center gap-1.5 mt-0.5"><Phone className="w-3 h-3" />{s.phone}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          className="input-field !py-1 !px-2 text-xs w-auto"
                          value={s.role ?? 'coach'}
                          disabled={busy || isMe}
                          title={isMe ? 'You cannot change your own role' : 'Change role'}
                          onChange={(e) => changeRole.mutate({ id: s.id, role: e.target.value })}
                        >
                          {ROLES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <span className={s.is_active === false ? 'badge-cancelled' : 'badge-active'}>
                          {s.is_active === false ? 'inactive' : 'active'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          {s.is_active !== false && !isMe && (
                            <button
                              title="Deactivate account" aria-label="Deactivate account" disabled={busy}
                              onClick={() => {
                                if (window.confirm(`Deactivate ${name}? They will no longer be able to sign in.`)) {
                                  deactivate.mutate(s.id)
                                }
                              }}
                              className="p-1.5 rounded-md text-text-dim hover:text-[rgb(var(--danger))] hover:bg-[rgb(var(--danger))]/12 transition-all duration-150 active:scale-95 disabled:opacity-40"
                            >
                              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewStaffDialog onClose={() => setShowNew(false)} />}
      {showPass && <ChangePasswordDialog onClose={() => setShowPass(false)} />}
    </div>
  )
}
